import * as React from 'react';
import styles from './index.less';
import { Modal } from 'antd';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import FComponentsLib from '@freelog/components-lib';
import FPhoneInput from '@/components/FPhoneInput';
import FInput_PinyinSafeTextCounter from '@/components/FInput_PinyinSafeTextCounter';
import fMessage from '@/components/fMessage';
import * as AHooks from 'ahooks';

interface FBindPhoneModalProps {
  open: boolean;

  onOk: (phone: string) => void;
}

function FBindPhoneModal({ open, onOk }: FBindPhoneModalProps) {
  const [$phoneInput, set$phoneInput, get$phoneInput] = FUtil.Hook.useGetState<string>('');
  const [$phoneInputError, set$phoneInputError, get$phoneInputError] =
    FUtil.Hook.useGetState<string>('');
  const [$phoneInputVerifyState, set$phoneInputVerifyState, get$phoneInputVerifyState] =
    FUtil.Hook.useGetState<string>('');
  const [$captchaInput, set$captchaInput, get$captchaInput] = FUtil.Hook.useGetState<string>('');
  const [$captchaWait, set$captchaWait, get$captchaWait] = FUtil.Hook.useGetState<number>(0);

  AHooks.useInterval(
    () => {
      set$captchaWait(get$captchaWait() - 1);
    },
    $captchaWait === 0 ? undefined : 1000,
  );

  return (
    <Modal
      title={
        // 绑定手机号
        FI18n.i18nNext.t('addphonenumber_title')
      }
      open={open}
      closable={false}
      footer={null}
      width={540}
    >
      <div className={styles.ModalContainer}>
        <div style={{ height: 15 }} />
        <FComponentsLib.FTipText
          text={
            // 手机号
            FI18n.i18nNext.t('addphonenumber_input_phonenumber')
          }
          type='third'
        />
        <div style={{ height: 5 }} />
        <FPhoneInput
          inputValue={$phoneInput}
          onChangeInput={async (value) => {
            set$phoneInput(value);
          }}
          onBlurInput={async () => {
            let bindPhone_PhoneInputError: string = '';
            if (get$phoneInput() === '') {
              bindPhone_PhoneInputError = FI18n.i18nNext.t('namingrules_phonenumber_required');
            } else if (!FUtil.Regexp.MOBILE_PHONE_NUMBER.test(get$phoneInput())) {
              bindPhone_PhoneInputError = '请输入正确格式的手机号';
            }

            if (bindPhone_PhoneInputError === '') {
              set$phoneInputVerifyState('verifying');
              const params: Parameters<typeof FServiceAPI.User.userDetails>[0] = {
                mobile: get$phoneInput(),
              };
              const { data }: { data: any } = await FServiceAPI.User.userDetails(params);
              if (data) {
                bindPhone_PhoneInputError = '手机号已被占用';
              }
            }

            set$phoneInputError(bindPhone_PhoneInputError);
            set$phoneInputVerifyState('verified');
          }}
          placeholder={FI18n.i18nNext.t('namingrules_phonenumber_required')}
        />
        {$phoneInputError && (
          <>
            <div style={{ height: 5 }} />
            <div style={{ color: 'red' }}>{$phoneInputError}</div>
          </>
        )}

        <div style={{ height: 25 }} />
        <FComponentsLib.FTipText
          text={
            // 验证码
            FI18n.i18nNext.t('common_input_verificationcode')
          }
          type='third'
        />
        <div style={{ height: 5 }} />
        <div className={styles.modalCaptcha}>
          <FInput_PinyinSafeTextCounter
            lengthLimit={-1}
            value={$captchaInput}
            onChangeValue={(value) => {
              set$captchaInput(value);
            }}
            placeholder={FI18n.i18nNext.t('common_input_verificationcode')}
            className={styles.modalCaptchaInput}
          />
          <FComponentsLib.FRectBtn
            disabled={
              $phoneInput === '' ||
              $phoneInputVerifyState !== 'verified' ||
              $phoneInputError !== '' ||
              $captchaWait > 0
            }
            style={{ width: 110 }}
            type='primary'
            onClick={async () => {
              set$captchaWait(60);

              const params: Parameters<typeof sentVerificationCode>[0] = {
                loginName: get$phoneInput(),
              };
              const result = await sentVerificationCode(params);
              if (!result) {
                set$captchaWait(0);
              }
            }}
          >
            {$captchaWait > 0
              ? `${$captchaWait}s`
              : FI18n.i18nNext.tAuto('common_btn_sendcode')}
          </FComponentsLib.FRectBtn>
        </div>
        <div style={{ height: 80 }} />
        <div className={styles.modalFooter}>
          <FComponentsLib.FRectBtn
            disabled={
              $phoneInput === '' ||
              $phoneInputVerifyState !== 'verified' ||
              $phoneInputError !== '' ||
              $captchaInput === ''
            }
            type='primary'
            onClick={async () => {
              if (!new RegExp(/^[0-9]*$/).test(get$captchaInput())) {
                fMessage('验证码必须全部为数字', 'error');
                return;
              }

              const params: Parameters<typeof FServiceAPI.User.updateMobileOrEmail>[0] = {
                newAuthCode: get$captchaInput(),
                newLoginName: get$phoneInput(),
              };

              const { ret, errCode, msg } = await FServiceAPI.User.updateMobileOrEmail(params);

              if (ret !== 0 || errCode !== 0) {
                return fMessage(msg, 'error');
              }

              fMessage('绑定成功', 'success');

              onOk(get$phoneInput());
            }}
          >
            {
              // 验证
              FI18n.i18nNext.tAuto('addphonenumber_btn_verify')
            }
          </FComponentsLib.FRectBtn>
        </div>
        <div style={{ height: 5 }} />
      </div>
    </Modal>
  );
}

export default FBindPhoneModal;

interface SentVerificationCode {
  loginName: string;
}

async function sentVerificationCode({ loginName }: SentVerificationCode): Promise<boolean> {
  const params: Parameters<typeof FServiceAPI.Captcha.sendVerificationCode>[0] = {
    loginName: loginName,
    authCodeType: 'updateMobileOrEmail',
  };
  const { errCode, msg } = await FServiceAPI.Captcha.sendVerificationCode(params);
  if (errCode === 0) {
    return true;
  }
  fMessage(msg, 'error');
  return false;
}
